import { X, Hand, Maximize2, Keyboard, Languages, Hash, Type, Sparkles } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';

interface KeyboardHelpModalProps {
  open: boolean;
  onClose: () => void;
  language: string;
  t: any;
}

export function KeyboardHelpModal({ open, onClose, language, t }: KeyboardHelpModalProps) {
  const helpText = t.keyboard?.help || {};
  const currentLang = language.toLowerCase();

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[85vh] p-0 gap-0 bg-gradient-to-br from-background via-background to-background/95 border-border/60">
        {/* Header with gradient */}
        <div className="relative border-b border-border/40 bg-gradient-to-r from-brand/5 via-brand/10 to-brand/5 px-6 py-4">
          <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-brand/30 to-transparent" />

          <DialogHeader>
            <DialogTitle className="flex items-center gap-3 text-xl font-bold">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-brand/20 border border-brand/30">
                <Keyboard className="h-5 w-5 text-brand" />
              </div>
              <div className="flex flex-col items-start gap-0.5">
                <span className="text-foreground">
                  {helpText.title || 'Premium Keyboard Guide'}
                </span>
                <span className="text-xs font-normal text-muted-foreground">
                  {currentLang.charAt(0).toUpperCase() + currentLang.slice(1)} • {helpText.subtitle || 'Complete feature guide'}
                </span>
              </div>
            </DialogTitle>
          </DialogHeader>

          <button
            onClick={onClose}
            className="absolute right-4 top-4 flex h-8 w-8 items-center justify-center rounded-full border border-border/40 bg-background/80 text-muted-foreground transition-all hover:border-danger/40 hover:bg-danger/10 hover:text-danger"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Content */}
        <ScrollArea className="h-full max-h-[calc(85vh-5rem)] px-6 py-6">
          <div className="space-y-6 pb-4">
            {/* Features Grid */}
            <div className="grid gap-4 sm:grid-cols-2">
              {/* Long Press */}
              <div className="group relative overflow-hidden rounded-xl border border-border/40 bg-surface p-4 transition-all hover:border-brand/30 hover:shadow-lg">
                <div className="pointer-events-none absolute inset-0 bg-surface opacity-0 transition-opacity group-hover:opacity-100" />
                <div className="relative space-y-2">
                  <div className="flex items-center gap-2">
                    <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-brand/10 border border-brand/20">
                      <Hand className="h-4 w-4 text-brand" />
                    </div>
                    <h3 className="font-semibold text-foreground">
                      {helpText.longPress?.title || 'Long Press'}
                    </h3>
                  </div>
                  <p className="text-sm leading-relaxed text-muted-foreground">
                    {helpText.longPress?.description || 'Press and hold any vowel or special character for 500ms to see all variants with diacritics.'}
                  </p>
                  <div className="flex flex-wrap gap-1.5 pt-1">
                    {['á', 'à', 'ǎ', 'â', 'ã', 'ā'].map((char) => (
                      <span
                        key={char}
                        className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-border/40 bg-white/[0.02] text-xs font-medium text-foreground"
                      >
                        {char}
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              {/* Compact Mode */}
              <div className="group relative overflow-hidden rounded-xl border border-border/40 bg-surface p-4 transition-all hover:border-brand/30 hover:shadow-lg">
                <div className="pointer-events-none absolute inset-0 bg-surface opacity-0 transition-opacity group-hover:opacity-100" />
                <div className="relative space-y-2">
                  <div className="flex items-center gap-2">
                    <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-info/10 border border-info/20">
                      <Maximize2 className="h-4 w-4 text-info" />
                    </div>
                    <h3 className="font-semibold text-foreground">
                      {helpText.modes?.title || 'Compact/Extended'}
                    </h3>
                  </div>
                  <p className="text-sm leading-relaxed text-muted-foreground">
                    {helpText.modes?.description || 'Toggle between compact view (essential characters) and extended view (all characters visible).'}
                  </p>
                </div>
              </div>

              {/* Layers */}
              <div className="group relative overflow-hidden rounded-xl border border-border/40 bg-surface p-4 transition-all hover:border-brand/30 hover:shadow-lg">
                <div className="pointer-events-none absolute inset-0 bg-surface opacity-0 transition-opacity group-hover:opacity-100" />
                <div className="relative space-y-2">
                  <div className="flex items-center gap-2">
                    <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-purple-500/10 border border-purple-400/20">
                      <Languages className="h-4 w-4 text-purple-400" />
                    </div>
                    <h3 className="font-semibold text-foreground">
                      {helpText.layers?.title || '6 Keyboard Layers'}
                    </h3>
                  </div>
                  <ul className="space-y-1 text-sm text-muted-foreground">
                    <li className="flex items-start gap-2">
                      <span className="text-brand">•</span>
                      <span>{helpText.layers?.african || 'African special characters'}</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-brand">•</span>
                      <span>{helpText.layers?.basic || 'Basic Latin alphabet'}</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-brand">•</span>
                      <span>{helpText.layers?.punctuation || 'Numbers & symbols'}</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-brand">•</span>
                      <span>{helpText.layers?.diacritics || 'Tonal marks'}</span>
                    </li>
                  </ul>
                </div>
              </div>

              {/* Quick Punctuation */}
              <div className="group relative overflow-hidden rounded-xl border border-border/40 bg-surface p-4 transition-all hover:border-brand/30 hover:shadow-lg">
                <div className="pointer-events-none absolute inset-0 bg-surface opacity-0 transition-opacity group-hover:opacity-100" />
                <div className="relative space-y-2">
                  <div className="flex items-center gap-2">
                    <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-success/10 border border-success/20">
                      <Hash className="h-4 w-4 text-success" />
                    </div>
                    <h3 className="font-semibold text-foreground">
                      {helpText.punctuation?.title || 'Quick Punctuation'}
                    </h3>
                  </div>
                  <p className="text-sm leading-relaxed text-muted-foreground">
                    {helpText.punctuation?.description || 'Fixed row at the bottom with instant access to common punctuation marks.'}
                  </p>
                  <div className="flex flex-wrap gap-1.5 pt-1">
                    {['.', ',', '?', '!', "'", ':', ';', '-'].map((char) => (
                      <span
                        key={char}
                        className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-border/40 bg-white/[0.02] text-xs font-medium text-foreground"
                      >
                        {char}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Supported Characters */}
            <div className="rounded-xl border border-border/40 bg-surface p-5">
              <div className="flex items-center gap-2 mb-3">
                <Sparkles className="h-5 w-5 text-brand" />
                <h3 className="font-semibold text-foreground">
                  {helpText.characters?.title || 'Supported Characters'}
                </h3>
              </div>
              <div className="grid gap-3 text-sm sm:grid-cols-2">
                <div>
                  <p className="font-medium text-foreground/80 mb-1">
                    {helpText.characters?.vowels || 'African Vowels:'}
                  </p>
                  <p className="font-mono text-muted-foreground">
                    ɛ ɔ ʋ ə ɩ ʊ ẹ ọ
                  </p>
                </div>
                <div>
                  <p className="font-medium text-foreground/80 mb-1">
                    {helpText.characters?.consonants || 'African Consonants:'}
                  </p>
                  <p className="font-mono text-muted-foreground">
                    ŋ ɣ ɖ ƒ ɓ ɗ ɠ ɲ ʃ ƴ ṣ
                  </p>
                </div>
                <div>
                  <p className="font-medium text-foreground/80 mb-1">
                    {helpText.characters?.digraphs || 'Digraphs:'}
                  </p>
                  <p className="font-mono text-muted-foreground">
                    gb kp ny hw xw
                  </p>
                </div>
                <div>
                  <p className="font-medium text-foreground/80 mb-1">
                    {helpText.characters?.tones || 'Tonal Marks:'}
                  </p>
                  <p className="font-mono text-muted-foreground">
                    á à ǎ â ã ā (+ all vowels)
                  </p>
                </div>
              </div>
            </div>

            {/* Tips */}
            <div className="rounded-xl border border-border/40 bg-surface p-5">
              <h3 className="font-semibold text-foreground mb-3">
                {helpText.tips?.title || 'Tips & Tricks'}
              </h3>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li className="flex items-start gap-2">
                  <span className="text-brand font-bold">→</span>
                  <span>{helpText.tips?.drag || 'Drag the keyboard anywhere on screen using the grip handle'}</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-brand font-bold">→</span>
                  <span>{helpText.tips?.resize || 'Resize keyboard width by dragging the right edge'}</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-brand font-bold">→</span>
                  <span>{helpText.tips?.shift || 'Use Shift (⇧) to type uppercase letters'}</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-brand font-bold">→</span>
                  <span>{helpText.tips?.indicator || 'Small orange dot on keys indicates long-press variants available'}</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-brand font-bold">→</span>
                  <span>{helpText.tips?.persistence || 'Your preferences (width, mode) are automatically saved'}</span>
                </li>
              </ul>
            </div>

            {/* Languages */}
            <div className="rounded-xl border border-brand/20 bg-surface p-5">
              <h3 className="font-semibold text-foreground mb-3">
                {helpText.languages?.title || 'Supported Languages'}
              </h3>
              <div className="flex flex-wrap gap-2">
                {[
                  'Ewe', 'Mina', 'Kabiyè', 'Kotokoli', 'Loso', 'Tem',
                  'Bassar', 'Nawdem', 'Moba', 'Fon', 'Yoruba', 'Fulfulde',
                  'Bambara', 'Français'
                ].map((lang) => (
                  <span
                    key={lang}
                    className="inline-flex items-center rounded-full border border-brand/30 bg-brand/10 px-3 py-1 text-xs font-medium text-brand"
                  >
                    {lang}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </ScrollArea>

        {/* Footer */}
        <div className="border-t border-border/40 bg-gradient-to-r from-brand/5 via-transparent to-brand/5 px-6 py-3">
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>{helpText.footer?.version || 'Premium Keyboard v2.0'}</span>
            <span className="flex items-center gap-1">
              <Sparkles className="h-3 w-3 text-brand" />
              {helpText.footer?.powered || 'Powered by Yodi'}
            </span>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
